/**
 * FuryBot V1 — Futures Prop Firm Decision Assistant
 * Standalone embedded chat widget. No LLM calls. All data from live backend.
 * Architecture: Structured answer objects reusable for both chat UI and /ask/ SEO pages.
 *
 * Flow logic extracted into /flows/ sub-modules for maintainability.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { X, Send, Copy, Check } from "lucide-react";

/** Lightweight markdown → JSX for bot messages. Handles **bold**, _italic_, and • bullets. */
const renderMarkdown = (text) => {
  if (!text) return null;
  // Split into segments by **bold** and _italic_ patterns
  const parts = [];
  const regex = /(\*\*(.+?)\*\*)|(_([^_]+?)_)/g;
  let lastIdx = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) parts.push({ type: "text", value: text.slice(lastIdx, match.index) });
    if (match[1]) parts.push({ type: "bold", value: match[2] });
    else if (match[3]) parts.push({ type: "italic", value: match[4] });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) parts.push({ type: "text", value: text.slice(lastIdx) });
  return parts.map((p, i) =>
    p.type === "bold" ? <strong key={i} className="font-semibold">{p.value}</strong>
    : p.type === "italic" ? <em key={i} className="opacity-80">{p.value}</em>
    : <span key={i}>{p.value}</span>
  );
};

import { BOT_API, DARK, LIGHT, CHIPS, FIRM_CHIP } from "./constants";
import { track } from "./utils/analytics";
import { buildAliasMap as buildAliasMapFn, detectFirm as detectFirmFn } from "./utils/firmDetection";
import { fetchFirms, loadCachedFirms } from "./utils/dataService";
import { CardRenderer } from "./cards/ChatCards";

// Flow handlers
import { createMatchFlowHandlers } from "./flows/matchFlow";
import { createDealsFlowHandlers } from "./flows/dealsFlow";
import { createCompareFlowHandlers } from "./flows/compareFlow";
import { createRulesFlowHandlers } from "./flows/rulesFlow";
import { createBeginnerFlowHandlers } from "./flows/beginnerFlow";
import { createToolsFlowHandlers } from "./flows/toolsFlow";
import { createMarketPulseHandlers } from "./flows/marketPulseFlow";
import { processFreeText } from "./flows/intentRouter";

// Theme hook
function useIsDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(document.documentElement.classList.contains("dark")));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

/* ═══ BOT COMPONENT ═══ */
export const FuryBot = () => {
  const isDark = useIsDark();
  const C = isDark ? DARK : LIGHT;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [firms, setFirms] = useState([]);
  const [promos, setPromos] = useState([]);
  const [input, setInput] = useState("");
  const [matchPrefs, setMatchPrefs] = useState({});
  const [matchStep, setMatchStep] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showTeaser, setShowTeaser] = useState(false);
  const [chipDismissed, setChipDismissed] = useState(false);
  const [cookieBarHeight, setCookieBarHeight] = useState(0);
  const [compareFirst, setCompareFirst] = useState(null);
  const [beginnerStep, setBeginnerStep] = useState(0);
  const location = useLocation();
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastActionTs = useRef(0);

  const firmsRef = useRef([]);
  const promosRef = useRef([]);
  const firmAliasMapRef = useRef({});
  const savedSessionRef = useRef(null);

  const buildAliasMap = useCallback((firmList) => {
    firmAliasMapRef.current = buildAliasMapFn(firmList);
    return firmAliasMapRef.current;
  }, []);

  const detectFirm = useCallback((text) => {
    return detectFirmFn(text, firmAliasMapRef.current);
  }, []);

  const addBot = useCallback((text, buttons = [], cards = []) => {
    setMessages(m => [...m, { role: "bot", text, buttons, cards, ts: Date.now() }]);
  }, []);

  const addUser = useCallback((text) => {
    setMessages(m => [...m, { role: "user", text, ts: Date.now() }]);
  }, []);

  const ensureFirms = useCallback(async () => {
    return fetchFirms(firmsRef, setFirms, buildAliasMap);
  }, [buildAliasMap]);

  // ── Flow handler context ──────────────────────────────────────────
  // Build a shared context object that flow handlers consume.
  // We use refs for mutable values that flows need access to.
  const getFlowCtx = useCallback(() => ({
    firmsRef, promosRef, firms, promos, addBot, addUser, setMessages,
    setFirms, setPromos, ensureFirms, detectFirm,
    matchPrefs, setMatchPrefs, setMatchStep,
    compareFirst, setCompareFirst,
    setBeginnerStep,
  }), [firms, promos, addBot, addUser, ensureFirms, detectFirm, matchPrefs, compareFirst]);

  // Instantiate flow handlers (re-created when context changes)
  const matchFlow = createMatchFlowHandlers(getFlowCtx());
  const dealsFlow = createDealsFlowHandlers(getFlowCtx());
  const compareFlow = createCompareFlowHandlers(getFlowCtx());
  const rulesFlow = createRulesFlowHandlers(getFlowCtx());
  const beginnerFlow = createBeginnerFlowHandlers(getFlowCtx());
  const toolsFlow = createToolsFlowHandlers(getFlowCtx());
  const marketPulseFlow = createMarketPulseHandlers(getFlowCtx());

  // ── Data loading ──────────────────────────────────────────────────
  useEffect(() => {
    loadCachedFirms(firmsRef, setFirms, buildAliasMap);
    axios.get(`${BOT_API}/firms`).then(r => { setFirms(r.data || []); firmsRef.current = r.data || []; buildAliasMap(r.data || []); sessionStorage.setItem("furybot_firms", JSON.stringify(r.data || [])); }).catch(() => {});
    axios.get(`${BOT_API}/promos/active`).then(r => { setPromos(r.data || []); promosRef.current = r.data || []; }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time mount init: load firms + build alias map
  }, []);
  useEffect(() => {
    const teaserShown = sessionStorage.getItem("furybot_teaser_shown");
    const teaserDismissed = sessionStorage.getItem("furybot_teaser_dismissed");
    // Suppress the floating teaser on narrow viewports (< 480px): on mobile
    // the 320px-wide tooltip overlays page CTAs and hurts UX. The FAB remains.
    const isNarrow = typeof window !== "undefined" && window.innerWidth < 480;
    if (!teaserShown && !teaserDismissed && !isNarrow) { setTimeout(() => setShowTeaser(true), 3000); sessionStorage.setItem("furybot_teaser_shown", "1"); }
  }, []);

  // Cookie consent bar height
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('[data-testid="cookie-consent"]');
      setCookieBarHeight(el ? el.offsetHeight : 0);
    };
    measure();
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Persist messages for resume
  useEffect(() => {
    if (messages.length) {
      sessionStorage.setItem("furybot_messages", JSON.stringify(messages.slice(-50)));
      sessionStorage.setItem("furybot_last_ts", String(Date.now()));
    }
  }, [messages]);

  // Auto-scroll
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Chat abandonment tracking
  useEffect(() => {
    const handleAbandon = () => {
      if (!open || messages.length < 2) return;
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.buttons?.length > 0) {
        track("chat_abandoned", { last_action: lastMsg?.text?.substring(0, 60), message_count: messages.length });
      }
    };
    const onVisChange = () => { if (document.hidden) handleAbandon(); };
    document.addEventListener("visibilitychange", onVisChange);
    window.addEventListener("beforeunload", handleAbandon);
    return () => {
      document.removeEventListener("visibilitychange", onVisChange);
      window.removeEventListener("beforeunload", handleAbandon);
    };
  }, [open, messages]);

  // ── Session management ────────────────────────────────────────────
  const showWelcome = () => {
    addBot("Hey! I'm FuryBot \u2014 I help traders compare futures prop firms, find live deals, and understand the rules. What are you looking for?", [
      { label: "Help me pick a firm", action: "flow_match" },
      { label: "Live prop firm deals", action: "flow_deals" },
      { label: "Market Pulse", action: "flow_market_pulse" },
      { label: "Compare two firms", action: "flow_compare" },
      { label: "Explain a rule to me", action: "flow_rules" },
      { label: "I'm new to prop trading", action: "flow_beginner" },
      { label: "Use a free tool", action: "flow_tools" },
    ]);
  };

  const handleOpen = () => {
    setOpen(true);
    setShowTeaser(false);
    track("chat_opened");
    if (messages.length === 0) {
      const savedMessages = sessionStorage.getItem("furybot_messages");
      const savedTs = sessionStorage.getItem("furybot_last_ts");
      if (savedMessages) {
        try {
          const parsed = JSON.parse(savedMessages);
          const lastTs = parseInt(savedTs || "0");
          const ageHours = (Date.now() - lastTs) / 3600000;
          if (parsed.length > 3 && ageHours < 24) {
            savedSessionRef.current = parsed;
            setMessages([{
              role: "bot",
              text: "Welcome back! Want to continue your last firm search or start fresh?",
              buttons: [
                { label: "Resume search", action: "resume_session" },
                { label: "Start fresh", action: "start_fresh" },
              ],
              ts: Date.now(),
            }]);
            return;
          }
        } catch {}
      }
      showWelcome();
    }
  };

  // ── Action routing ────────────────────────────────────────────────
  const processAction = (action, data) => {
    if (action === "flow_match") matchFlow.startMatchFlow();
    else if (action === "flow_deals") dealsFlow.startDealsFlow();
    else if (action === "flow_compare") compareFlow.startCompareFlow();
    else if (action === "flow_rules") rulesFlow.startRulesFlow();
    else if (action === "flow_beginner") beginnerFlow.startBeginnerFlow();
    else if (action === "flow_tools") toolsFlow.startToolsFlow();
    else if (action === "flow_market_pulse") marketPulseFlow.startMarketPulse();
    else if (action.startsWith("match_")) matchFlow.handleMatchStep(action, data);
    else if (action === "deals_both_compared") { try { const d = JSON.parse(data); dealsFlow.showBothFirmDeals(d.s1, d.s2, d.size); } catch {} }
    else if (action === "deals_all") dealsFlow.showAllDeals();
    else if (action === "deals_firm") dealsFlow.showFirmPicker("deals");
    else if (action.startsWith("deal_firm_")) dealsFlow.showFirmDeal(action.replace("deal_firm_", ""));
    else if (action === "compare_pick1") dealsFlow.showFirmPicker("compare1");
    else if (action.startsWith("compare1_")) compareFlow.handleCompare1(action.replace("compare1_", ""));
    else if (action.startsWith("compare2_")) compareFlow.handleCompare2(data || action.replace("compare2_", ""));
    else if (action === "compare_size") { try { const d = JSON.parse(data); compareFlow.showCompareResult(d.s1, d.s2, d.size); } catch {} }
    else if (action === "rule_explain") rulesFlow.showRuleExplanation(data);
    else if (action === "beginner_quick") { setMatchPrefs({ experience: "first", priority: "drawdown" }); matchFlow.startMatchFlow(); }
    else if (action === "beginner_walk") { beginnerFlow.showBeginnerStep(parseInt(data) || 0); }
    else if (action === "home") showWelcome();
    else if (action === "resume_session") {
      if (savedSessionRef.current) { setMessages(savedSessionRef.current); savedSessionRef.current = null; }
      else showWelcome();
    }
    else if (action === "start_fresh") {
      savedSessionRef.current = null;
      sessionStorage.removeItem("furybot_messages");
      sessionStorage.removeItem("furybot_last_ts");
      setMessages([]);
      showWelcome();
    }
    else if (action === "restart_match") { setMatchPrefs({}); setMatchStep(null); matchFlow.startMatchFlow(); }
    else if (action === "retry_match") {
      try { const prefs = JSON.parse(data); matchFlow.showMatchResults(prefs); } catch { matchFlow.startMatchFlow(); }
    }
  };

  // ── Input handlers ────────────────────────────────────────────────
  const handleButton = (btn) => {
    const now = Date.now();
    if (now - lastActionTs.current < 400) return;
    lastActionTs.current = now;
    addUser(btn.label);
    track("option_selected", { action: btn.action, label: btn.label });
    processAction(btn.action, btn.data);
  };

  const handleSend = () => {
    if (!input.trim()) return;
    const now = Date.now();
    if (now - lastActionTs.current < 400) return;
    lastActionTs.current = now;
    const text = input.trim();
    setInput("");
    addUser(text);
    track("free_text_question", { text });
    handleFreeText(text);
  };

  const handleFreeText = async (text) => {
    const f = await ensureFirms();
    // Clear active guided flow state
    setMatchStep(null);
    setCompareFirst(null);
    setBeginnerStep(0);
    // Clear stale flow buttons
    setMessages(m => {
      const updated = [...m];
      if (updated.length > 0) {
        const lastBot = [...updated].reverse().find(msg => msg.role === "bot" && msg.buttons?.length > 0);
        if (lastBot) {
          const idx = updated.lastIndexOf(lastBot);
          updated[idx] = { ...lastBot, buttons: [] };
        }
      }
      return updated;
    });

    processFreeText(text, {
      firms: f, addBot, detectFirm,
      startMatchFlow: matchFlow.startMatchFlow,
      startDealsFlow: dealsFlow.startDealsFlow,
      startCompareFlow: compareFlow.startCompareFlow,
      startRulesFlow: rulesFlow.startRulesFlow,
      startBeginnerFlow: beginnerFlow.startBeginnerFlow,
      startToolsFlow: toolsFlow.startToolsFlow,
      startMarketPulse: marketPulseFlow.startMarketPulse,
      showFirmDeal: dealsFlow.showFirmDeal,
    });
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => { setCopiedCode(code); setTimeout(() => setCopiedCode(null), 2000); });
  };

  // ── Context-aware smart chip ──────────────────────────────────────
  const currentPath = location.pathname;
  const smartChip = !chipDismissed && !open ? (
    CHIPS[currentPath] || (currentPath.startsWith("/firms/") ? FIRM_CHIP : null)
  ) : null;

  const handleChipClick = (flow) => {
    setChipDismissed(true);
    setOpen(true);
    track("chat_opened", { source: "chip" });
    if (messages.length === 0) showWelcome();
    setTimeout(() => processAction(flow), 300);
  };

  const dismissTeaser = (e) => {
    e.stopPropagation();
    setShowTeaser(false);
    setChipDismissed(true);
    sessionStorage.setItem("furybot_teaser_dismissed", "1");
  };

  /* ═══ RENDER ═══ */
  if (!open) return (
    <>
      {(showTeaser || smartChip) && (
        <div className="fixed right-4 z-[10000]" style={{ bottom: `${84 + cookieBarHeight}px`, maxWidth: "min(320px, calc(100vw - 32px))" }}>
          <div onClick={() => smartChip ? handleChipClick(smartChip.flow) : handleOpen()} className="relative flex items-center gap-3 px-5 py-3.5 rounded-2xl cursor-pointer transition-all hover:border-blue-500/40" style={{ background: "#161926", border: "1px solid #353A4D", boxShadow: "0 12px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)" }}>
            <span className="text-sm font-medium" style={{ color: "#E8E9ED" }}>{smartChip ? smartChip.text : "Need help picking a firm?"}</span>
            <button onClick={dismissTeaser} className="p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0" style={{ color: "#8B8FA3" }}>
              <X size={14} />
            </button>
            <div className="absolute -bottom-[7px] right-7 w-3.5 h-3.5 rotate-45" style={{ background: "#161926", borderRight: "1px solid #353A4D", borderBottom: "1px solid #353A4D" }} />
          </div>
        </div>
      )}
      <button onClick={handleOpen} className="fixed right-4 z-[9999] w-[60px] h-[60px] rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ bottom: `${16 + cookieBarHeight}px`, background: "transparent" }} aria-label="Open FuryBot" data-testid="furybot-launcher">
        <span className="absolute inset-[-6px] rounded-full" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.2) 0%, rgba(220,38,38,0.12) 50%, transparent 70%)", animation: "furyPulse 3s ease-in-out infinite" }} />
        <img src="/furybot-icon.png" alt="FuryBot AI" className="w-[60px] h-[60px] rounded-full" style={{ boxShadow: "0 0 18px rgba(37,99,235,0.35), 0 0 18px rgba(220,38,38,0.25)" }} />
        <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#0E1019]" style={{ background: C.success, boxShadow: `0 0 8px ${C.success}88` }}><span className="absolute inset-0 rounded-full animate-ping" style={{ background: C.success, opacity: 0.4 }} /></span>
      </button>
      <style>{`@keyframes furyPulse { 0%, 100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }`}</style>
    </>
  );

  return (
    <div className="fixed right-4 z-[9999] flex flex-col shadow-2xl" data-testid="furybot-chat" style={{ bottom: `${16 + cookieBarHeight}px`, width: "min(385px, calc(100vw - 32px))", height: "min(600px, calc(100vh - 100px))", background: C.bg, borderRadius: "16px", border: `1px solid ${C.border}` }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full flex items-center justify-center relative" style={{ background: "radial-gradient(circle at 35% 40%, #111827, #0E1019)", border: "1px solid #252A3A" }}><span className="text-[10px] font-black text-white" style={{ textShadow: "0 0 6px rgba(37,99,235,0.4)" }}>FF</span></div>
          <div>
            <div className="flex items-center gap-1.5"><span className="text-sm font-bold" style={{ color: C.text }}>FuryBot</span><span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${C.blue}22`, color: C.blue }}>AI</span></div>
            <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: C.success }} /><span className="text-[10px]" style={{ color: C.dim }}>Online</span></div>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg transition-colors hover:bg-white/5" data-testid="furybot-close"><X size={16} style={{ color: C.dim }} /></button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" style={{ scrollbarWidth: "thin" }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} gap-2`}>
            {msg.role === "bot" && <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ background: "radial-gradient(circle at 35% 40%, #111827, #0E1019)", border: "1px solid #252A3A" }}><span className="text-[7px] font-black text-white">FF</span></div>}
            <div className="max-w-[85%]">
              {msg.text && <div className="px-3.5 py-2.5 rounded-xl text-sm leading-relaxed whitespace-pre-line" style={{ background: msg.role === "user" ? `${C.blue}15` : C.bubble, color: C.text, borderTopLeftRadius: msg.role === "bot" ? "4px" : undefined, borderTopRightRadius: msg.role === "user" ? "4px" : undefined }}>{msg.role === "bot" ? renderMarkdown(msg.text) : msg.text}</div>}
              {msg.cards?.map((card, ci) => <CardRenderer key={ci} card={card} onCopy={handleCopy} copiedCode={copiedCode} onNavigate={() => setOpen(false)} />)}
              {msg.buttons?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{msg.buttons.map((btn, bi) => (
                <button key={bi} onClick={() => btn.action === "link" ? window.open(btn.data, "_self") : handleButton(btn)} className="px-3 py-2 rounded-full text-xs font-medium transition-all hover:brightness-125" style={{ background: `${C.blue}12`, color: C.blue, border: `1px solid ${C.blue}25`, minHeight: "36px" }}>
                  {btn.label}
                </button>
              ))}</div>}
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-3 pb-3 pt-1">
        <div className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSend()} placeholder="Ask about prop firms..." className="flex-1 bg-transparent text-sm outline-none" style={{ color: C.text }} data-testid="furybot-input" />
          <button onClick={handleSend} disabled={!input.trim()} className="p-1.5 rounded-lg transition-colors disabled:opacity-30" style={{ background: C.red }} data-testid="furybot-send"><Send size={14} className="text-white" /></button>
        </div>
        <p className="text-center mt-1.5" style={{ fontSize: "9px", color: C.dim }}>Powered by FuturesFury · Not trading advice</p>
      </div>
    </div>
  );
};
